import { ITEM_CONTRACT } from '@/api/contracts/items';
import type {
  AiSearchQuery,
  ItemListQuery,
  RawAiSearchResponse,
  RawItemDetail,
  RawItemListItem,
  RawItemListResponse,
} from '@/api/contracts/items';
import { resolvePathTemplate } from '@/api/contracts';
import type { AiSearchResponse, ItemDetail, ItemFilters, ItemListItem, PaginatedResponse } from '@/types';
import { normalizeItemFilterSearch, type ItemFilterSearch } from '@/lib/validation';
import { api } from './client';

function normalizeFilters(filters: ItemFilters): ItemFilters {
  return {
    types: filters.types,
    ui: filters.ui,
    tags: filters.tags,
    tagGroups: filters.tagGroups,
  };
}

export function normalizeItemListItem(item: RawItemListItem): ItemListItem {
  return {
    id: item.id,
    slug: item.slug,
    title: item.title,
    excerpt: item.summary,
    previewImageUrl: item.previewImageUrl ?? null,
    previewImageWidth: item.previewImageWidth ?? null,
    previewImageHeight: item.previewImageHeight ?? null,
    savedCount: item.savedCount,
    type: item.types[0]?.slug === 'section' || item.types[0]?.slug === 'full-page' || item.types[0]?.slug === 'block'
      ? item.types[0].slug
      : 'section',
    primaryRole: item.primaryRole ?? null,
    tags: item.tags,
    uiTags: item.uiCategories.map((term) => term.slug),
    searchScore: item.searchScore ?? undefined,
    tailwindVersion: item.tailwindVersion,
    isFeatured: item.featured,
  };
}

function normalizeDetailItem(item: RawItemDetail): ItemDetail {
  const listItem = normalizeItemListItem(item);

  return {
    ...listItem,
    description: item.summary,
    code: item.code,
    adminEditUrl: item.adminEditUrl ?? null,
    previewViewports: item.previewViewports,
  };
}

function normalizeAiSearchResponse(response: RawAiSearchResponse): AiSearchResponse {
  return {
    query: response.query,
    mode: response.mode,
    plan: response.plan,
    meta: response.meta,
    notes: response.notes,
    items: response.items.map(normalizeItemListItem),
  };
}

export async function listItems(filters: ItemFilterSearch, page: number = 1): Promise<PaginatedResponse<ItemListItem>> {
  const tags = filters.tags?.trim() ? filters.tags : undefined;
  const query: ItemListQuery = {
    type: filters.type,
    ui: tags ? undefined : filters.ui,
    tags,
    page,
  };

  const response = await api<RawItemListResponse>(ITEM_CONTRACT.list.path, {
    method: ITEM_CONTRACT.list.method,
    params: query,
    responseSchema: ITEM_CONTRACT.list.schemas.response,
  });

  return {
    data: response.items.map(normalizeItemListItem),
    total: response.pagination.totalItems,
    totalPages: response.pagination.totalPages,
  };
}

export async function getItem(itemId: number): Promise<ItemDetail> {
  const response = await api<RawItemDetail>(resolvePathTemplate(ITEM_CONTRACT.detail.path, { id: itemId }), {
    method: ITEM_CONTRACT.detail.method,
    responseSchema: ITEM_CONTRACT.detail.schemas.response,
  });

  return normalizeDetailItem(response);
}

export async function updateItemThumbnail(itemId: number, file: File): Promise<ItemDetail> {
  const formData = new FormData();
  formData.append('thumbnail', file);

  const response = await api<RawItemDetail>(
    resolvePathTemplate(ITEM_CONTRACT.updateThumbnail.path, { id: itemId }),
    {
      method: ITEM_CONTRACT.updateThumbnail.method,
      body: formData,
      responseSchema: ITEM_CONTRACT.updateThumbnail.schemas.response,
    },
  );

  return normalizeDetailItem(response);
}

export async function getItemFilters(): Promise<ItemFilters> {
  const response = await api<ItemFilters>(ITEM_CONTRACT.filters.path, {
    method: ITEM_CONTRACT.filters.method,
    responseSchema: ITEM_CONTRACT.filters.schemas.response,
  });

  return normalizeFilters(response);
}

export async function searchItemsWithAi(filters: ItemFilterSearch, limit: number = 24): Promise<AiSearchResponse> {
  const normalizedFilters = normalizeItemFilterSearch(filters);
  const tags = normalizedFilters.tags?.trim() ? normalizedFilters.tags : undefined;
  const query: AiSearchQuery = {
    query: normalizedFilters.query ?? '',
    limit,
    type: normalizedFilters.type,
    tags,
  };

  const response = await api<RawAiSearchResponse>(ITEM_CONTRACT.aiSearch.path, {
    method: ITEM_CONTRACT.aiSearch.method,
    params: query,
    responseSchema: ITEM_CONTRACT.aiSearch.schemas.response,
  });

  return normalizeAiSearchResponse(response);
}
